import { Indexable } from "@/utility/model"
import * as D3 from "d3"

export const Donut3D: Indexable<any> = {}

function pieTop(d: any, rx: number, ry: number, ir: number) {
    if (d.endAngle - d.startAngle === 0) return "M 0 0"
    var sx = rx * Math.cos(d.startAngle),
        sy = ry * Math.sin(d.startAngle),
        ex = rx * Math.cos(d.endAngle),
        ey = ry * Math.sin(d.endAngle)

    var ret = []
    ret.push(
        "M",
        sx,
        sy,
        "A",
        rx,
        ry,
        "0",
        d.endAngle - d.startAngle > Math.PI ? 1 : 0,
        "1",
        ex,
        ey,
        "L",
        ir * ex,
        ir * ey
    )
    ret.push(
        "A",
        ir * rx,
        ir * ry,
        "0",
        d.endAngle - d.startAngle > Math.PI ? 1 : 0,
        "0",
        ir * sx,
        ir * sy,
        "z"
    )
    return ret.join(" ")
}

function pieOuter(d: any, rx: number, ry: number, h: number) {
    var startAngle = d.startAngle > Math.PI ? Math.PI : d.startAngle
    var endAngle = d.endAngle > Math.PI ? Math.PI : d.endAngle

    var sx = rx * Math.cos(startAngle),
        sy = ry * Math.sin(startAngle),
        ex = rx * Math.cos(endAngle),
        ey = ry * Math.sin(endAngle)

    var ret = []
    ret.push(
        "M",
        sx,
        h + sy,
        "A",
        rx,
        ry,
        "0 0 1",
        ex,
        h + ey,
        "L",
        ex,
        ey,
        "A",
        rx,
        ry,
        "0 0 0",
        sx,
        sy,
        "z"
    )
    return ret.join(" ")
}

function pieInner(d: any, rx: number, ry: number, h: number, ir: number) {
    var startAngle = d.startAngle < Math.PI ? Math.PI : d.startAngle
    var endAngle = d.endAngle < Math.PI ? Math.PI : d.endAngle

    var sx = ir * rx * Math.cos(startAngle),
        sy = ir * ry * Math.sin(startAngle),
        ex = ir * rx * Math.cos(endAngle),
        ey = ir * ry * Math.sin(endAngle)

    var ret = []
    ret.push(
        "M",
        sx,
        sy,
        "A",
        ir * rx,
        ir * ry,
        "0 0 1",
        ex,
        ey,
        "L",
        ex,
        h + ey,
        "A",
        ir * rx,
        ir * ry,
        "0 0 0",
        sx,
        h + sy,
        "z"
    )
    return ret.join(" ")
}

function getPercent(d: { endAngle: number; startAngle: number }) {
    return (
        ((1000 * (d.endAngle - d.startAngle)) / (Math.PI * 2) / 10).toFixed(2) +
        "%"
    )
}

Donut3D.transition = function (
    id: string,
    data: any,
    rx: number,
    ry: number,
    h: number,
    ir: number
) {
    function arcTweenInner(this: any, a: any) {
        var i = D3.interpolate(this._current, a)
        this._current = i(0)
        return function (t: any) {
            return pieInner(i(t), rx + 0.5, ry + 0.5, h, ir)
        }
    }
    function arcTweenTop(this: any, a: any) {
        var i = D3.interpolate(this._current, a)
        this._current = i(0)
        return function (t: any) {
            return pieTop(i(t), rx, ry, ir)
        }
    }
    function arcTweenOuter(this: any, a: any) {
        var i = D3.interpolate(this._current, a)
        this._current = i(0)
        return function (t: any) {
            return pieOuter(i(t), rx - 0.5, ry - 0.5, h)
        }
    }
    function textTweenX(this: any, a: any) {
        var i = D3.interpolate(this._current, a) as (t: number) => any
        this._current = i(0)
        return function (t: any) {
            return String(0.6 * rx * Math.cos(0.5 * (i(t).startAngle + i(t).endAngle)))
        }
    }
    function textTweenY(this: any, a: any) {
        var i = D3.interpolate(this._current, a) as (t: number) => any
        this._current = i(0)
        return function (t: any) {
            return String(0.6 * rx * Math.sin(0.5 * (i(t).startAngle + i(t).endAngle)))
        }
    }

    var _data = D3
        .pie()
        .sort(null)
        .value(function (d: any) {
            return d.value
        })(data)

    D3.select("#" + id)
        .selectAll(".innerSlice")
        .data(_data)
        .transition()
        .duration(750)
        .attrTween("d", arcTweenInner)

    D3.select("#" + id)
        .selectAll(".topSlice")
        .data(_data)
        .transition()
        .duration(750)
        .attrTween("d", arcTweenTop)

    D3.select("#" + id)
        .selectAll(".outerSlice")
        .data(_data)
        .transition()
        .duration(750)
        .attrTween("d", arcTweenOuter)

    D3.select("#" + id)
        .selectAll(".percent")
        .data(_data)
        .transition()
        .duration(750)
        .attrTween("x", textTweenX)
        .attrTween("y", textTweenY)
        .text(getPercent)
}

Donut3D.draw = function (
    id: string,
    data: (number | { valueOf(): number })[],
    x: string /*center x*/,
    y: string /*center y*/,
    rx: number /*radius x*/,
    ry: number /*radius y*/,
    h: number /*height*/,
    ir: number /*inner radius*/
) {
    var _data = D3
        .pie()
        .sort(null)
        .value(function (d:any) {
            return d.value
        })(data)

    var slices = D3
        .select("#" + id)
        .append("g")
        .attr("transform", "translate(" + x + "," + y + ")")
        .attr("class", "slices")

    slices
        .selectAll(".innerSlice")
        .data(_data)
        .enter()
        .append("path")
        .attr("class", "innerSlice")
        .style("fill",  (d: any) =>  {
            return D3.hsl(d.data.color).darker(0.7).formatHex()
        })
        .attr("d", function (d: { startAngle: number; endAngle: number }) {
            return pieInner(d, rx + 0.5, ry + 0.5, h, ir)
        })
        .each(function (this: any, d: any) {
            this._current = d
        })

    slices
        .selectAll(".topSlice")
        .data(_data)
        .enter()
        .append("path")
        .attr("class", "topSlice")
        .style("fill", function (d: any) {
            return d.data.color
        })
        .style("stroke", function (d: any) {
            return d.data.color
        })
        .attr("d", function (d: { endAngle: number; startAngle: number }) {
            return pieTop(d, rx, ry, ir)
        })
        .each(function (this: any, d: any) {
            this._current = d
        })

    slices
        .selectAll(".outerSlice")
        .data(_data)
        .enter()
        .append("path")
        .attr("class", "outerSlice")
        .style("fill", function (d: any) {
            return D3.hsl(d.data.color).darker(0.7).formatHex()
        })
        .attr("d", function (d: { startAngle: number; endAngle: number }) {
            return pieOuter(d, rx - 0.5, ry - 0.5, h)
        })
        .each(function (this: any, d: any) {
            this._current = d
        })

    slices
        .selectAll(".percent")
        .data(_data)
        .enter()
        .append("text")
        .attr("fill", "white")
        .attr("class", "percent")
        .attr("text-anchor", "middle")
        .attr("x", function (d: { startAngle: any; endAngle: any }) {
            return 0.5 * rx * Math.cos(0.5 * (d.startAngle + d.endAngle))
        })
        .attr("y", function (d: { startAngle: any; endAngle: any }) {
            return 0.6 * ry * Math.sin(0.5 * (d.startAngle + d.endAngle))
        })
        .text(getPercent)
        .each(function (this: any, d: any) {
            this._current = d
        })
}
